import { useLocation, Link } from "react-router-dom";
import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import NotificationsBell from "@/components/NotificationsBell";
import { useScope } from "@/context/ScopeContext";
import { roleLabel } from "@/lib/roles";
import { LogOut, ChevronRight, Search, PanelLeft, Building2 } from "lucide-react";

const LABELS = {
  app: "Home",
  leads: "Leads",
  clients: "Clients",
  quotations: "Quotations",
  estimations: "Estimations",
  projects: "Projects",
  employees: "Employees",
  roadmap: "Architecture & Roadmap",
  settings: "Settings",
  ai: "AI Studio",
  invoices: "Invoices",
  payments: "Payments",
  hours: "Production Hours",
  qa: "Quality Assurance",
  vendors: "Vendors",
  assets: "Asset Lifecycle",
  estimations: "Estimations",
  handovers: "Handovers",
  "handover-inbox": "Handover Inbox",
  profile: "My Profile",
  "reset-data": "Reset Data",
  "backup-restore": "Backup & Restore",
  departments: "Departments",
  designations: "Designations",
  branches: "Branches",
  roles: "Roles & Permissions",
  branding: "Branding",
  production: "Production Settings",
  changelog: "Changelog",
  administration: "Administration",
  "resource-planning": "Resource Planning",
  workload: "Workload",
  tasks: "Tasks",
  revisions: "Revisions",
  recruitment: "Recruitment",
  payroll: "Payroll",
  documents: "Documents",
  "client-portal": "Client Portal",
  "employee-portal": "Employee Portal",
  reports: "Reports Studio",
  audit: "Audit Logs",
};

function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "U";
}

export default function Header({ onToggleSidebar }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { activeCompany, activeDivision, companies, divisions, setActiveCompanyId, setActiveDivisionId } = useScope();
  const divisionsInActiveCompany = useMemo(
    () => (activeCompany ? divisions.filter((d) => d.company_id === activeCompany.id) : []),
    [divisions, activeCompany],
  );
  const qc = useQueryClient();
  const parts = location.pathname.split("/").filter(Boolean);
  const crumbs = parts.map((p, i) => {
    const to = "/" + parts.slice(0, i + 1).join("/");
    let label = LABELS[p] || p;
    // If this is a UUID-ish detail segment, look up the friendly number from React Query cache.
    if (!LABELS[p] && /^[a-f0-9]{20,}$/i.test(p)) {
      const parent = parts[i - 1];
      if (parent === "quotations") {
        const cached = qc.getQueryData(["quotation", p]);
        if (cached?.number) label = cached.number;
        else label = p.slice(0, 8) + "…";
      } else {
        label = p.slice(0, 8) + "…";
      }
    }
    return { label, to };
  });

  return (
    <header className="h-16 border-b border-zinc-200 bg-white flex items-center gap-4 px-6 shrink-0" data-testid="app-header">
      <button
        onClick={onToggleSidebar}
        className="hidden md:grid w-9 h-9 place-items-center rounded-md hover:bg-zinc-100 transition-colors"
        data-testid="sidebar-toggle"
        aria-label="Toggle sidebar"
      >
        <PanelLeft className="w-4 h-4 text-zinc-600" />
      </button>
      <nav className="flex items-center gap-1.5 text-sm text-zinc-500 min-w-0" data-testid="breadcrumbs">
        {crumbs.length === 0 ? (
          <span className="font-medium text-zinc-900">Dream Home ERP</span>
        ) : (
          crumbs.map((c, i) => (
            <span key={c.to} className="flex items-center gap-1.5 min-w-0">
              {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-zinc-300 shrink-0" />}
              <Link
                to={c.to}
                className={
                  i === crumbs.length - 1
                    ? "text-zinc-900 font-medium truncate"
                    : "hover:text-zinc-900 transition-colors truncate"
                }
                data-testid={`crumb-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c.label}
              </Link>
            </span>
          ))
        )}
      </nav>
      <div className="ml-auto flex items-center gap-3">
        {activeCompany && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-[11px] font-normal" data-testid="scope-switcher-trigger">
                <Building2 className="w-3 h-3 text-zinc-500" />
                <span className="font-medium text-zinc-900">{activeCompany.code}</span>
                {activeDivision && (
                  <>
                    <span className="text-zinc-300">·</span>
                    <span className="text-zinc-700">{activeDivision.name}</span>
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64" data-testid="scope-switcher-menu">
              <DropdownMenuLabel className="text-[10px] text-zinc-500 uppercase tracking-wider">Company</DropdownMenuLabel>
              {companies.map((c) => (
                <DropdownMenuItem key={c.id} onClick={() => setActiveCompanyId(c.id)} className="text-xs">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${c.id === activeCompany.id ? "bg-emerald-500" : "bg-zinc-300"}`} />
                    <span>{c.name}</span>
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] text-zinc-500 uppercase tracking-wider">Division</DropdownMenuLabel>
              {divisionsInActiveCompany.map((d) => (
                <DropdownMenuItem key={d.id} onClick={() => setActiveDivisionId(d.id)} className="text-xs">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${d.id === activeDivision?.id ? "bg-emerald-500" : "bg-zinc-300"}`} />
                    <span>{d.name}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <div className="hidden md:flex items-center gap-2 text-sm text-zinc-400 border border-zinc-200 rounded-md px-2.5 py-1.5">
          <Search className="w-3.5 h-3.5" />
          <span className="font-mono text-[11px]">⌘K · Search</span>
        </div>
        <NotificationsBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-10 px-2 gap-2" data-testid="user-menu-trigger">
              <Avatar className="w-7 h-7 border border-zinc-200">
                {user?.picture && <AvatarImage src={user.picture} alt={user?.name} />}
                <AvatarFallback className="text-[11px] bg-zinc-900 text-white font-heading">
                  {initials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block text-left leading-tight">
                <div className="text-[12px] font-medium text-zinc-900">{user?.name}</div>
                <div className="text-[10px] text-zinc-500">{roleLabel(user?.role)}</div>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="user-menu">
            <DropdownMenuLabel>
              <div className="text-[12px] font-medium text-zinc-900">{user?.name}</div>
              <div className="text-[11px] text-zinc-500 font-normal">{user?.email}</div>
              <div className="text-[10px] text-zinc-500 mt-1">{roleLabel(user?.role)}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/app/profile" data-testid="menu-profile">My Profile</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/app/settings" data-testid="menu-settings">Settings</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/app/roadmap" data-testid="menu-roadmap">Architecture & Roadmap</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600 focus:text-red-600">
              <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
